import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectItem,
  Spinner,
  Button,
  Chip,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import {
  useGetPhoneNumberListQuery,
  useGetProviderListQuery,
  getDefaultNumberList,
  getNumberDisplay,
  formatPhoneDisplay,
  getProviderLabel,
  isNumberInDefaultList,
} from '@circuitry-ai/doc-data';
import type { PhoneNumberItem } from '@circuitry-ai/doc-data';
import { CtyButton, CtyShowToast, checkType, SimpleDeleteConfirmationModal, ErrorBoundary, useAgGridStatePersistence } from '@circuitry-ai/doc-ui';
import { PurchasePhoneNumberSidePanel } from './PurchasePhoneNumberSidePanel';

export interface PhoneNumberRow {
  item: PhoneNumberItem;
  isDefault: boolean;
  id: string;
}

const DeleteConfirmationModalWithErrorBoundary = ErrorBoundary(
  SimpleDeleteConfirmationModal as unknown as React.ComponentType<Record<string, unknown>>
);

const PAGE_SIZE = 10;

export const PhoneNumbersTab: React.FC = () => {
  const { t } = useTranslation();
  const [providerFilter, setProviderFilter] = useState<string>('all');
  const [addPanelOpen, setAddPanelOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [numberToDelete, setNumberToDelete] = useState<PhoneNumberRow | null>(null);

  const { data, isLoading, refetch, isFetching } = useGetPhoneNumberListQuery(
    providerFilter === 'all' ? undefined : { provider: providerFilter }
  );
  const { data: providerList } = useGetProviderListQuery();

  const defaultNumberList = useMemo(
    () => getDefaultNumberList(data?.default_number),
    [data?.default_number]
  );
  const numbersList = data?.numbers ?? [];

  const displayList = useMemo(() => {
    const defaults = defaultNumberList.map((item) => ({ item, isDefault: true }));
    const nonDefaults = numbersList.filter(
      (n) => !isNumberInDefaultList(n, defaultNumberList)
    );
    return [
      ...defaults,
      ...nonDefaults.map((item) => ({ item, isDefault: false })),
    ];
  }, [defaultNumberList, numbersList]);

  const rowData = useMemo<PhoneNumberRow[]>(
    () =>
      displayList.map(({ item, isDefault }) => ({
        item,
        isDefault,
        id: getNumberDisplay(item) || String(item?.id),
      })),
    [displayList]
  );

  const providerOptions = useMemo(() => {
    const list = providerList?.providers ?? [];
    const entries = Array.isArray(list)
      ? list.map((p) => (typeof p === 'string' ? { value: p, label: p } : { value: (p as { provider?: string }).provider ?? '', label: (p as { label?: string; provider?: string }).label ?? (p as { provider?: string }).provider ?? '' }))
      : [];
    return [
      { value: 'all', label: t('All') },
      ...entries.filter((o) => o.value),
    ];
  }, [providerList, t]);

  const ownedNumberSet = useMemo(() => {
    const set = new Set<string>();
    displayList.forEach(({ item }) => {
      const n = getNumberDisplay(item);
      if (n && n !== '—') set.add(n.replace(/\D/g, ''));
    });
    return set;
  }, [displayList]);

  const { augmentGridReady, mergeColumnDefs, suppressDragLeaveHidesColumns } =
    useAgGridStatePersistence('phone-numbers-tab-table');

  const onGridReady = useCallback((params: GridReadyEvent) => {
    augmentGridReady(params, (p) => {
      p?.api?.sizeColumnsToFit?.();
    });
  }, [augmentGridReady]);

  const handleDeleteRequested = useCallback((row: PhoneNumberRow) => {
    if (row.isDefault) return;
    setNumberToDelete(row);
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(
    (_row: PhoneNumberRow | null | undefined) => {
      setDeleteModalOpen(false);
      setNumberToDelete(null);
      // TODO: wire to delete phone number API when available
      CtyShowToast(checkType.info, t('Remove number is not available yet.'));
      refetch();
    },
    [refetch, t]
  );

  const handleDeleteModalClose = useCallback(() => {
    setDeleteModalOpen(false);
    setNumberToDelete(null);
  }, []);

  const statusDeleteCellRenderer = useCallback(
    (params: { data: PhoneNumberRow }) => {
      const { data } = params;
      if (!data) return null;
      const isDefault = data.isDefault;
      return (
        <div className="flex items-center gap-2 h-full py-1">
          {isDefault ? (
            <Chip size="sm" color="success" variant="flat">
              {t('Default')}
            </Chip>
          ) : (
            <span className="text-default-600 text-sm">{t('Active')}</span>
          )}
          {!isDefault && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteRequested(data);
              }}
              className="p-1 rounded text-danger hover:bg-danger/10 min-w-8 h-8 flex items-center justify-center"
              aria-label={t('Delete')}
            >
              <Icon icon="mdi:delete-outline" className="w-4 h-4" />
            </button>
          )}
        </div>
      );
    },
    [t, handleDeleteRequested]
  );

  const columnDefs = useMemo<ColDef<PhoneNumberRow>[]>(
    () => [
      {
        colId: 'phoneNumber',
        headerName: t('Phone Number'),
        field: 'id',
        sortable: true,
        flex: 1,
        valueFormatter: (params) => {
          const row = params.data;
          return row ? formatPhoneDisplay(getNumberDisplay(row.item)) : '—';
        },
        cellRenderer: (params: { data: PhoneNumberRow }) => {
          const row = params.data;
          if (!row) return '—';
          const displayNum = formatPhoneDisplay(getNumberDisplay(row.item));
          return (
            <span className="font-medium text-foreground">{displayNum}</span>
          );
        },
      },
      {
        colId: 'provider',
        headerName: t('Provider'),
        field: 'item.provider',
        sortable: true,
        flex: 1,
        valueFormatter: (params) => {
          const row = params.data;
          return row ? getProviderLabel(row.item?.provider) : '—';
        },
      },
      {
        colId: 'status',
        field: 'isDefault',
        headerName: t('Status'),
        sortable: false,
        flex: 1,
        cellStyle: { display: 'flex', alignItems: 'center' },
        cellRenderer: statusDeleteCellRenderer,
      },
    ],
    [t, statusDeleteCellRenderer]
  );

  const gridStyle = useMemo(
    () =>
      ({
        width: '100%',
        minHeight: 240,
        '--ag-border-color': 'var(--nextui-divider)',
        '--ag-cell-horizontal-border': '1px solid var(--ag-border-color)',
      }) as React.CSSProperties,
    []
  );
  const paginationPageSizeSelector = useMemo(() => [10, 20, 40, 50], []);
  const columnDefsWithSort = useMemo(() => mergeColumnDefs(columnDefs), [columnDefs, mergeColumnDefs]);

  if (isLoading) {
    return (
      <div className="flex flex-1 min-h-[200px] border border-divider rounded-lg bg-default-50 items-center justify-center gap-2">
        <Spinner size="sm" />
        <span className="text-default-500 text-sm">
          {t('Loading phone numbers...')}
        </span>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4 p-4 bg-default-50/50 rounded-lg overflow-auto">
      {/* Title row: "Phone Numbers" (highlighted) with Add Phone Number on the right */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {t('Phone Numbers')}
          </h2>
          <p className="text-sm text-default-500 mt-0.5">
            {t('Manage inbound and outbound phone numbers used by voice agents.')}
          </p>
        </div>
        <CtyButton
          color="primary"
          onPress={() => setAddPanelOpen(true)}
          startContent={<Icon icon="mdi:plus" className="w-4 h-4" />}
        >
          {t('Add Phone Number')}
        </CtyButton>
      </div>

      {/* Table toolbar: dropdown and actions on top-right of table */}
      <div className="flex flex-wrap items-center justify-end gap-2">
        <span className="text-xs font-medium text-default-500 uppercase tracking-wide">
          {t('PROVIDER')}
        </span>
        <Select
          aria-label={t('Filter by provider')}
          className="min-w-[120px] max-w-[180px]"
          selectedKeys={[providerFilter]}
          onSelectionChange={(keys) => {
            const v = Array.from(keys)[0];
            if (v) setProviderFilter(String(v));
          }}
          size="sm"
          variant="bordered"
        >
          {providerOptions.map((opt) => (
            <SelectItem key={opt.value}>{opt.label}</SelectItem>
          ))}
        </Select>
        <Button
          size="sm"
          variant="bordered"
          onPress={() => refetch()}
          isLoading={isFetching}
          startContent={
            <Icon icon="mdi:refresh" fontSize={16} height={16} width={16} />
          }
        >
          {t('Refresh')}
        </Button>
        <Button
          size="sm"
          variant="bordered"
          startContent={
            <Icon icon="mdi:upload" fontSize={16} height={16} width={16} />
          }
        >
          {t('Import')}
        </Button>
      </div>

      {/* AG Grid table */}
      <div className="ag-theme-alpine" style={gridStyle}>
        <AgGridReact<PhoneNumberRow>
          suppressDragLeaveHidesColumns={suppressDragLeaveHidesColumns}
          rowData={rowData}
          columnDefs={columnDefsWithSort}
          defaultColDef={{ resizable: true }}
          headerHeight={40}
          domLayout="autoHeight"
          onGridReady={onGridReady}
          getRowId={(params) => params.data.id}
          pagination
          paginationPageSize={PAGE_SIZE}
          paginationPageSizeSelector={paginationPageSizeSelector}
          suppressRowClickSelection
          alwaysShowHorizontalScroll
        />
      </div>

      <PurchasePhoneNumberSidePanel
        isOpen={addPanelOpen}
        onClose={() => setAddPanelOpen(false)}
        onPurchaseSuccess={() => refetch()}
        ownedNumberSet={ownedNumberSet}
      />

      {deleteModalOpen && (
        <DeleteConfirmationModalWithErrorBoundary
          isOpen={deleteModalOpen}
          onOpenChange={(open: boolean) => { if (!open) handleDeleteModalClose(); }}
          onConfirm={() => handleDeleteConfirm(numberToDelete)}
          isConfirmLoading={false}
          requireTypedConfirmation={false}
          title={t('Delete Confirmation')}
          body={
            <p className="text-sm font-semibold">
              {t('Please confirm that')}{' '}
              <span className="text-danger">
                {numberToDelete ? formatPhoneDisplay(getNumberDisplay(numberToDelete.item)) : ''}
              </span>{' '}
              {t('is no longer needed.')}
            </p>
          }
        />
      )}
    </div>
  );
};

export default PhoneNumbersTab;
